import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen, Scale, HelpCircle, ArrowRight } from 'lucide-react';

const LearnMoreSection = () => {
  const resources = [
    {
      category: 'PCP Finance',
      title: 'Understanding PCP Car Finance Claims',
      description: 'Learn how PCP finance works, what discretionary commission is, and why millions of UK drivers may be owed compensation.',
      links: [
        { label: 'What is PCP Car Finance?', href: '/pcp/what-is-pcp-car-finance' },
        { label: 'How PCP is Mis-sold', href: '/pcp/how-pcp-is-mis-sold' },
        { label: 'FCA Compensation Scheme', href: '/pcp/fca-motor-finance-compensation-scheme' },
      ],
      mainCta: { label: 'Start Your PCP Claim', href: '/pcp-car-finance-claims' },
      icon: Scale,
      color: 'accent'
    },
    {
      category: 'GAP Insurance',
      title: 'Understanding GAP Insurance Claims',
      description: 'Discover how GAP insurance is sold, why it may have been mis-sold to you, and how to claim a refund.',
      links: [
        { label: 'What is GAP Insurance?', href: '/gap-insurance/what-is-gap-insurance' },
        { label: 'How GAP is Sold with PCP', href: '/gap-insurance/how-gap-is-sold-with-pcp' },
        { label: 'FCA Action on GAP', href: '/gap-insurance/fca-action-on-gap-insurance' },
      ],
      mainCta: { label: 'Start Your GAP Claim', href: '/gap-insurance-claims' },
      icon: BookOpen,
      color: 'gold'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-muted/30">
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
            Resources
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Learn About Your Claim
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Understand your rights and learn how the car finance scandal affects you. 
            Our guides explain everything in plain English.
          </p>
        </div>

        {/* Resource Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {resources.map((resource, index) => (
            <Card 
              key={index}
              className="border-border hover:border-accent/50 transition-all duration-300 hover:shadow-xl overflow-hidden"
            >
              <CardContent className="p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg bg-${resource.color}/10 flex items-center justify-center`}>
                    <resource.icon className={`w-5 h-5 text-${resource.color}`} />
                  </div>
                  <span className={`text-sm font-semibold text-${resource.color} uppercase tracking-wider`}>
                    {resource.category}
                  </span>
                </div>

                {/* Title & Description */}
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                  {resource.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {resource.description}
                </p>

                {/* Links */}
                <div className="space-y-2 mb-6">
                  {resource.links.map((link, linkIndex) => (
                    <a 
                      key={linkIndex}
                      href={link.href}
                      className="flex items-center gap-2 text-sm text-foreground hover:text-accent transition-colors group"
                    >
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                      {link.label}
                    </a>
                  ))}
                </div>

                {/* Main CTA */}
                <a 
                  href={resource.mainCta.href}
                  className={`inline-flex items-center gap-2 text-sm font-semibold text-${resource.color} hover:underline`}
                >
                  {resource.mainCta.label}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Link */}
        <div className="text-center mt-12">
          <a 
            href="/pcp/pcp-claims-faq" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-accent transition-colors"
          >
            <HelpCircle className="w-5 h-5" />
            <span>View all frequently asked questions</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export { LearnMoreSection };
